import { USER_SCOPE_ENUM, UserScope } from "interfaces";
import { routes } from "constants/routes";
import {
  ChangePasswordPage,
  ContractsPage,
  ContractInfoPage,
  BidsPage,
  BidInfoPage,
  ContractCreatePage,
  InformationPage,
  ReportsPage,
  CompanyInfoPage,
  CompaniesPage,
  CompanyCreatePage,
  UserCreatePage,
  UsersPage,
  UserInfoPage,
  SettingsPage,
  ContractEditPage,
  UserEditPage,
  CompanyEditPage,
  ProfilePage,
  ComplianceReviewPage,
  PendingActionsPage,
  BidsPageWithBalance,
  MarkdownGuide,
  AmlChecksPage,
  WalletsWhitelistPage,
} from "pages";
import { Navigate } from "react-router-dom";
import { baseLayout, emptyLayout } from "./Helpers";
import { NominalAccountPage } from "pages/NominalAccount";
import { MessageTemplates } from "pages/SystemAdminPages/MessageTemplates";
import { WebSettings } from "pages/SystemAdminPages/WebSettings";
import { DocumentTemplates } from "pages/SystemAdminPages/DocumentTemplates";
import { HintsManagement } from "pages/SystemAdminPages/HintsManagement";

export interface IRouteItem {
  path: string;
  element: React.ReactNode;
  replace?: boolean;
  roles: UserScope[];
}

export const allRoutesItems: IRouteItem[] = [
  {
    path: routes.bids,
    element: baseLayout(BidsPage),
    roles: [
      USER_SCOPE_ENUM.JUNIOR_CUSTOMER,
      USER_SCOPE_ENUM.SENIOR_CUSTOMER,
      USER_SCOPE_ENUM.JUNIOR_OPERATOR,
      USER_SCOPE_ENUM.SENIOR_OPERATOR,
      USER_SCOPE_ENUM.RISK_MANAGER,
      USER_SCOPE_ENUM.JUNIOR_LP,
      USER_SCOPE_ENUM.LP_RISK_MANAGER,
      USER_SCOPE_ENUM.SENIOR_LP,
    ],
  },
  {
    path: `${routes.bids}/:id`,
    element: baseLayout(BidInfoPage, { showBack: true }),
    roles: [
      USER_SCOPE_ENUM.JUNIOR_CUSTOMER,
      USER_SCOPE_ENUM.SENIOR_CUSTOMER,
      USER_SCOPE_ENUM.JUNIOR_OPERATOR,
      USER_SCOPE_ENUM.SENIOR_OPERATOR,
      USER_SCOPE_ENUM.RISK_MANAGER,
      USER_SCOPE_ENUM.JUNIOR_LP,
      USER_SCOPE_ENUM.LP_RISK_MANAGER,
      USER_SCOPE_ENUM.SENIOR_LP,
    ],
  },
  {
    path: routes.contracts,
    element: baseLayout(ContractsPage),
    roles: [
      USER_SCOPE_ENUM.JUNIOR_CUSTOMER,
      USER_SCOPE_ENUM.SENIOR_CUSTOMER,
      USER_SCOPE_ENUM.JUNIOR_OPERATOR,
      USER_SCOPE_ENUM.SENIOR_OPERATOR,
      USER_SCOPE_ENUM.RISK_MANAGER,
    ],
  },
  {
    path: `${routes.contracts}/:id`,
    element: baseLayout(ContractInfoPage, { showBack: true }),
    roles: [
      USER_SCOPE_ENUM.JUNIOR_CUSTOMER,
      USER_SCOPE_ENUM.SENIOR_CUSTOMER,
      USER_SCOPE_ENUM.JUNIOR_OPERATOR,
      USER_SCOPE_ENUM.SENIOR_OPERATOR,
      USER_SCOPE_ENUM.RISK_MANAGER,
    ],
  },
  {
    path: `${routes.contracts}/edit/:id`,
    element: baseLayout(ContractEditPage, { showBack: true }),
    roles: [USER_SCOPE_ENUM.SENIOR_OPERATOR, USER_SCOPE_ENUM.SENIOR_CUSTOMER, USER_SCOPE_ENUM.JUNIOR_CUSTOMER],
  },
  {
    path: `${routes.contracts}/create`,
    element: baseLayout(ContractCreatePage, { showBack: true }),
    roles: [USER_SCOPE_ENUM.JUNIOR_CUSTOMER, USER_SCOPE_ENUM.SENIOR_CUSTOMER, USER_SCOPE_ENUM.SENIOR_OPERATOR],
  },
  {
    path: routes.info,
    element: baseLayout(InformationPage),
    roles: [
      USER_SCOPE_ENUM.JUNIOR_CUSTOMER,
      USER_SCOPE_ENUM.SENIOR_CUSTOMER,
      USER_SCOPE_ENUM.JUNIOR_OPERATOR,
      USER_SCOPE_ENUM.SENIOR_OPERATOR,
      USER_SCOPE_ENUM.RISK_MANAGER,
      USER_SCOPE_ENUM.JUNIOR_LP,
      USER_SCOPE_ENUM.LP_RISK_MANAGER,
      USER_SCOPE_ENUM.SENIOR_LP,
      USER_SCOPE_ENUM.SYSTEM_ADMIN,
      USER_SCOPE_ENUM.USER_ADMIN,
    ],
  },
  {
    path: routes.reports,
    element: baseLayout(ReportsPage),
    roles: [
      USER_SCOPE_ENUM.JUNIOR_CUSTOMER,
      USER_SCOPE_ENUM.SENIOR_CUSTOMER,
      USER_SCOPE_ENUM.JUNIOR_OPERATOR,
      USER_SCOPE_ENUM.SENIOR_OPERATOR,
      USER_SCOPE_ENUM.RISK_MANAGER,
      USER_SCOPE_ENUM.JUNIOR_LP,
      USER_SCOPE_ENUM.LP_RISK_MANAGER,
      USER_SCOPE_ENUM.SENIOR_LP,
      USER_SCOPE_ENUM.SYSTEM_ADMIN,
      USER_SCOPE_ENUM.USER_ADMIN,
    ],
  },
  {
    path: `${routes.companies}/:id`,
    element: baseLayout(CompanyInfoPage, { showBack: true }),
    roles: [
      USER_SCOPE_ENUM.JUNIOR_CUSTOMER,
      USER_SCOPE_ENUM.SENIOR_CUSTOMER,
      USER_SCOPE_ENUM.JUNIOR_OPERATOR,
      USER_SCOPE_ENUM.SENIOR_OPERATOR,
      USER_SCOPE_ENUM.RISK_MANAGER,
      USER_SCOPE_ENUM.JUNIOR_LP,
      USER_SCOPE_ENUM.LP_RISK_MANAGER,
      USER_SCOPE_ENUM.SENIOR_LP,
      USER_SCOPE_ENUM.SYSTEM_ADMIN,
      USER_SCOPE_ENUM.USER_ADMIN,
    ],
  },
  {
    path: routes.companies,
    element: baseLayout(CompaniesPage),
    roles: [
      USER_SCOPE_ENUM.JUNIOR_OPERATOR,
      USER_SCOPE_ENUM.SENIOR_OPERATOR,
      USER_SCOPE_ENUM.RISK_MANAGER,
      USER_SCOPE_ENUM.USER_ADMIN,
    ],
  },
  {
    path: `${routes.companies}/create`,
    element: baseLayout(CompanyCreatePage, { showBack: true }),
    roles: [USER_SCOPE_ENUM.USER_ADMIN],
  },
  {
    path: `${routes.companies}/edit/:id`,
    element: baseLayout(CompanyEditPage, { showBack: true }),
    roles: [USER_SCOPE_ENUM.USER_ADMIN],
  },
  {
    path: `${routes.users}/create`,
    element: baseLayout(UserCreatePage, { showBack: true }),
    roles: [USER_SCOPE_ENUM.USER_ADMIN],
  },
  {
    path: `${routes.users}/edit/:id`,
    element: baseLayout(UserEditPage, { showBack: true }),
    roles: [USER_SCOPE_ENUM.USER_ADMIN],
  },
  {
    path: routes.users,
    element: baseLayout(UsersPage),
    roles: [USER_SCOPE_ENUM.RISK_MANAGER, USER_SCOPE_ENUM.USER_ADMIN],
  },
  {
    path: `${routes.users}/:id`,
    element: baseLayout(UserInfoPage, { showBack: true }),
    roles: [
      USER_SCOPE_ENUM.JUNIOR_CUSTOMER,
      USER_SCOPE_ENUM.SENIOR_CUSTOMER,
      USER_SCOPE_ENUM.JUNIOR_OPERATOR,
      USER_SCOPE_ENUM.SENIOR_OPERATOR,
      USER_SCOPE_ENUM.RISK_MANAGER,
      USER_SCOPE_ENUM.USER_ADMIN,
      USER_SCOPE_ENUM.SYSTEM_ADMIN,
      USER_SCOPE_ENUM.JUNIOR_LP,
      USER_SCOPE_ENUM.LP_RISK_MANAGER,
      USER_SCOPE_ENUM.SENIOR_LP,
    ],
  },
  {
    path: `${routes.profile}`,
    element: baseLayout(ProfilePage, { showBack: true }),
    roles: [
      USER_SCOPE_ENUM.JUNIOR_CUSTOMER,
      USER_SCOPE_ENUM.SENIOR_CUSTOMER,
      USER_SCOPE_ENUM.JUNIOR_OPERATOR,
      USER_SCOPE_ENUM.SENIOR_OPERATOR,
      USER_SCOPE_ENUM.RISK_MANAGER,
      USER_SCOPE_ENUM.USER_ADMIN,
      USER_SCOPE_ENUM.SYSTEM_ADMIN,
      USER_SCOPE_ENUM.JUNIOR_LP,
      USER_SCOPE_ENUM.LP_RISK_MANAGER,
      USER_SCOPE_ENUM.SENIOR_LP,
    ],
  },
  {
    path: `${routes.settings}`,
    element: baseLayout(SettingsPage),
    roles: [
      USER_SCOPE_ENUM.JUNIOR_CUSTOMER,
      USER_SCOPE_ENUM.SENIOR_CUSTOMER,
      USER_SCOPE_ENUM.JUNIOR_OPERATOR,
      USER_SCOPE_ENUM.SENIOR_OPERATOR,
      USER_SCOPE_ENUM.RISK_MANAGER,
      USER_SCOPE_ENUM.USER_ADMIN,
      USER_SCOPE_ENUM.SYSTEM_ADMIN,
      USER_SCOPE_ENUM.JUNIOR_LP,
      USER_SCOPE_ENUM.LP_RISK_MANAGER,
      USER_SCOPE_ENUM.SENIOR_LP,
    ],
  },
  {
    path: `${routes.pendingActions}`,
    element: baseLayout(PendingActionsPage),
    roles: [
      USER_SCOPE_ENUM.JUNIOR_CUSTOMER,
      USER_SCOPE_ENUM.SENIOR_CUSTOMER,
      USER_SCOPE_ENUM.JUNIOR_OPERATOR,
      USER_SCOPE_ENUM.SENIOR_OPERATOR,
      USER_SCOPE_ENUM.JUNIOR_LP,
      USER_SCOPE_ENUM.LP_RISK_MANAGER,
      USER_SCOPE_ENUM.SENIOR_LP,
    ],
  },
  {
    path: `${routes.bidsWithBalance}`,
    element: baseLayout(BidsPageWithBalance),
    roles: [
      USER_SCOPE_ENUM.JUNIOR_CUSTOMER,
      USER_SCOPE_ENUM.SENIOR_CUSTOMER,
      USER_SCOPE_ENUM.JUNIOR_OPERATOR,
      USER_SCOPE_ENUM.SENIOR_OPERATOR,
      USER_SCOPE_ENUM.RISK_MANAGER,
      USER_SCOPE_ENUM.JUNIOR_LP,
      USER_SCOPE_ENUM.LP_RISK_MANAGER,
      USER_SCOPE_ENUM.SENIOR_LP,
    ],
  },
  {
    path: routes.markdownGuide,
    element: baseLayout(MarkdownGuide),
    roles: [
      USER_SCOPE_ENUM.JUNIOR_OPERATOR,
      USER_SCOPE_ENUM.SENIOR_OPERATOR,
      USER_SCOPE_ENUM.RISK_MANAGER,
      USER_SCOPE_ENUM.SYSTEM_ADMIN,
      USER_SCOPE_ENUM.USER_ADMIN,
    ],
  },
  {
    path: routes.amlChecks,
    element: baseLayout(AmlChecksPage),
    roles: [
      USER_SCOPE_ENUM.SENIOR_CUSTOMER,
      USER_SCOPE_ENUM.JUNIOR_CUSTOMER,
      USER_SCOPE_ENUM.SENIOR_LP,
      USER_SCOPE_ENUM.JUNIOR_LP,
      USER_SCOPE_ENUM.LP_RISK_MANAGER,
      USER_SCOPE_ENUM.SENIOR_OPERATOR,
      USER_SCOPE_ENUM.JUNIOR_OPERATOR,
      USER_SCOPE_ENUM.RISK_MANAGER,
    ],
  },

  {
    path: routes.nominalAccount,
    element: baseLayout(NominalAccountPage),
    roles: [
      USER_SCOPE_ENUM.JUNIOR_CUSTOMER,
      USER_SCOPE_ENUM.SENIOR_CUSTOMER,
      USER_SCOPE_ENUM.JUNIOR_OPERATOR,
      USER_SCOPE_ENUM.SENIOR_OPERATOR,
      USER_SCOPE_ENUM.RISK_MANAGER,
      USER_SCOPE_ENUM.JUNIOR_LP,
      USER_SCOPE_ENUM.LP_RISK_MANAGER,
      USER_SCOPE_ENUM.SENIOR_LP,
    ],
  },
  {
    path: routes.interfaceCustomization,
    element: baseLayout(WebSettings),
    roles: [USER_SCOPE_ENUM.SYSTEM_ADMIN],
  },
  {
    path: routes.documentTemplates,
    element: baseLayout(DocumentTemplates),
    roles: [USER_SCOPE_ENUM.SENIOR_OPERATOR, USER_SCOPE_ENUM.JUNIOR_OPERATOR],
  },
  {
    path: routes.messageTemplates,
    element: baseLayout(MessageTemplates),
    roles: [USER_SCOPE_ENUM.SYSTEM_ADMIN],
  },
  {
    path: routes.hintsManagement,
    element: baseLayout(HintsManagement),
    roles: [USER_SCOPE_ENUM.SYSTEM_ADMIN],
  },

  {
    path: routes.walletsWhitelist,
    element: baseLayout(WalletsWhitelistPage),
    roles: [USER_SCOPE_ENUM.LP_RISK_MANAGER, USER_SCOPE_ENUM.RISK_MANAGER],
  },
  // redirect
  {
    path: "*",
    element: <Navigate to={routes.pendingActions} replace />,
    roles: [
      USER_SCOPE_ENUM.JUNIOR_CUSTOMER,
      USER_SCOPE_ENUM.SENIOR_CUSTOMER,
      USER_SCOPE_ENUM.JUNIOR_OPERATOR,
      USER_SCOPE_ENUM.SENIOR_OPERATOR,
      USER_SCOPE_ENUM.JUNIOR_LP,
      USER_SCOPE_ENUM.LP_RISK_MANAGER,
      USER_SCOPE_ENUM.SENIOR_LP,
    ],
  },

  {
    path: "*",
    element: <Navigate to={routes.info} replace />,
    roles: [USER_SCOPE_ENUM.SYSTEM_ADMIN],
  },

  {
    path: "*",
    element: <Navigate to={routes.companies} replace />,
    roles: [USER_SCOPE_ENUM.USER_ADMIN],
  },

  {
    path: "*",
    element: <Navigate to={routes.bids} replace />,
    roles: [USER_SCOPE_ENUM.RISK_MANAGER],
  },

  // change password
  {
    path: routes.changePassword,
    element: emptyLayout(ChangePasswordPage),
    roles: [USER_SCOPE_ENUM.PASSWORD_CHANGE],
  },
  {
    path: "*",
    element: <Navigate to={routes.changePassword} replace />,
    roles: [USER_SCOPE_ENUM.PASSWORD_CHANGE],
  },

  // COMPANY_DOC_UPLOADING

  {
    path: routes.uploadingAnOffer,
    element: emptyLayout(ComplianceReviewPage, true),
    roles: [USER_SCOPE_ENUM.COMPANY_DOC_UPLOADING],
  },
  {
    path: "*",
    element: <Navigate to={routes.uploadingAnOffer} replace />,
    roles: [USER_SCOPE_ENUM.COMPANY_DOC_UPLOADING],
  },
];
